/**
 * HOOK: useWizard
 *
 * Extracts the multi-step wizard navigation system from App.jsx — 1 useState
 * (formStep), 3 useMemo validations (step1Valid, step2Valid, step3Valid),
 * 3 navigation callbacks (goNext, goBack, goToStep), and a step label
 * derivation (currentStepLabel).
 *
 * Flat convivencia-friendly return: all exports are top-level named values
 * so App.jsx can destructure them directly alongside current inline state
 * without renaming.
 */

import { useState, useMemo, useCallback, useEffect } from 'react'

const STORAGE_KEY = 'wizardFormStep'
const MAX_STEP = 5

/**
 * @param {object}   params
 * @param {object}   params.expediente       — for sexo-based branching and field validation
 * @param {string}   params.modoHerramienta  — 'base' | 'avanzado'
 * @param {string}   params.jurisdiccion     — for step3Valid
 * @param {object}   params.activeRules      — for step3Valid (activeRules.caseMeta.scenarioKey)
 */
export default function useWizard({ expediente, modoHerramienta, jurisdiccion, activeRules }) {
  const [formStep, setFormStep] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY)
      const parsed = Number(saved)
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= MAX_STEP ? parsed : 1
    } catch {
      return 1
    }
  })

  // Persist formStep to sessionStorage on change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(formStep))
    } catch {
      // ignore quota errors
    }
  }, [formStep])

  // ── Step validation (3 useMemo) ─────────────────────────────────

  const step1Valid = useMemo(() => {
    const cuilOk = modoHerramienta === 'avanzado'
      ? String(expediente.cuilCuit || '').replace(/\D/g, '').length === 11
      : true
    return Boolean(
      String(expediente.apellido || '').trim() &&
      String(expediente.nombre || '').trim() &&
      cuilOk &&
      String(expediente.fechaNacimiento || '').trim() &&
      String(expediente.sexo || '').trim()
    )
  }, [modoHerramienta, expediente.apellido, expediente.nombre, expediente.cuilCuit, expediente.fechaNacimiento, expediente.sexo])

  const step2Valid = useMemo(() => {
    if (modoHerramienta === 'avanzado') {
      return expediente.cobraAUH === 'si' || expediente.cobraAUH === 'no'
    }
    return true
  }, [modoHerramienta, expediente.cobraAUH])

  const step3Valid = useMemo(() => {
    if (modoHerramienta === 'base') {
      return Boolean(String(expediente.fechaCese || '').trim())
    }
    const falloValido = Boolean(String(expediente.fallo || '').trim())
    const tieneBeneficioPrevio = Boolean(expediente?.tieneBeneficioPrevio)
    const otorgamientoValido = !tieneBeneficioPrevio || Boolean(String(expediente.fechaOtorgamientoBeneficio || '').trim())
    const docenteMode = Boolean(expediente.esDocente || String(activeRules?.caseMeta?.scenarioKey || '').includes('DOCENTE'))
    const docenteCargoOk = !docenteMode || (
      Number(expediente?.datosDocentes?.sueldoBrutoCargoCese || 0) > 0 &&
      (Number(expediente?.datosDocentes?.mesesEnCargoContinuos || 0) >= 60 || Number(expediente?.datosDocentes?.mesesEnCargoAlternados || 0) >= 120)
    )
    const pensionType = String(expediente?.datosPension?.tipoPension || '').toUpperCase()
    const pensionOk = !pensionType || (
      String(expediente?.datosPension?.fechaFallecimiento || '').trim() &&
      (pensionType !== 'DERIVADA' || Number(expediente?.datosPension?.haberJubiladoCausante || 0) > 0)
    )
    return Boolean(
      String(jurisdiccion || '').trim() &&
      String(expediente.fechaCese || '').trim() &&
      falloValido &&
      otorgamientoValido &&
      docenteCargoOk &&
      pensionOk
    )
  }, [modoHerramienta, jurisdiccion, expediente.fechaCese, expediente.fallo, expediente.fechaOtorgamientoBeneficio, expediente.tieneBeneficioPrevio, expediente.esDocente, expediente?.datosDocentes?.sueldoBrutoCargoCese, expediente?.datosDocentes?.mesesEnCargoContinuos, expediente?.datosDocentes?.mesesEnCargoAlternados, expediente?.datosPension?.tipoPension, expediente?.datosPension?.fechaFallecimiento, expediente?.datosPension?.haberJubiladoCausante, activeRules?.caseMeta?.scenarioKey])

  // ── Step label ──────────────────────────────────────────────────

  const currentStepLabel = useMemo(() => {
    if (formStep === 0) return 'Paso 1 de 6: Selección de modo'
    return `Paso ${formStep + 1} de 6`
  }, [formStep])

  // ── Navigation callbacks ────────────────────────────────────────

  const goNext = useCallback(() => {
    setFormStep((prev) => {
      if (prev === 1) {
        // Step 1 → skip step 2 (Tareas de Cuidado) for sexo 'M'
        return expediente.sexo === 'M' ? 3 : 2
      }
      return prev + 1
    })
  }, [expediente.sexo])

  const goBack = useCallback(() => {
    setFormStep((prev) => {
      if (prev === 3) {
        // Step 3 → back to step 1 for sexo 'M' (was at step 1 before step 3)
        return expediente.sexo === 'M' ? 1 : 2
      }
      return prev - 1
    })
  }, [expediente.sexo])

  const goToStep = useCallback((step) => {
    setFormStep((prev) => {
      const num = Number(step)
      if (!Number.isFinite(num)) return prev
      return Math.max(1, Math.min(num, MAX_STEP))
    })
  }, [])

  // ── Return flat object ──────────────────────────────────────────

  return {
    formStep,
    setFormStep,
    step1Valid,
    step2Valid,
    step3Valid,
    goNext,
    goBack,
    goToStep,
    currentStepLabel,
  }
}
